import { theme } from "@config";
import { BaseUser, OrgScopedBaseUser } from "@models";
import { ArchiveOutlined, Edit, UnarchiveOutlined } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import GenericButton from "src/components/GenericButton/GenericButton";
import { getOrgMembership, getUserDisplayName } from "src/utils/user";

export default function UsersTable({
  rows,
  orgId,
  currentUserId,
  handleEditUserName,
  handleArchiveUser,
  handleUnarchiveUser,
  disableCellSelection = false,
}: {
  rows: OrgScopedBaseUser[];
  orgId: string;
  currentUserId: string;
  handleEditUserName: (user?: BaseUser) => void;
  handleArchiveUser: (userId: string) => void;
  handleUnarchiveUser: (userId: string) => void;
  disableCellSelection?: boolean;
}) {
  const isTestEnvironment = process.env.NODE_ENV === "test";

  const defineRows = rows.map((user) => {
    const isCurrentUser = user.id === currentUserId;
    const orgMembership = getOrgMembership(user, orgId);
    const isArchived = orgMembership?.activated === false;
    const functionalGroupMemberships =
      orgMembership?.functionalGroupMemberships ?? [];

    return {
      id: user.id,
      archived: isArchived,
      groups: functionalGroupMemberships,
      name: getUserDisplayName(user),
      createdAt: user.createdAt.toDateString(),
      email: user.email,
      status: (
        <Chip
          className="text-sm"
          label={`${isArchived ? "Archived" : user.authId ? "Active" : "Pending"}`}
          color={isArchived ? "default" : user.authId ? "success" : "warning"}
          size="tiny"
        />
      ),
      actions: (
        <div className="flex w-full items-center justify-end gap-1">
          <GenericButton
            size="small"
            onClick={() => handleEditUserName(user)}
            isDisabled={isArchived}
            dataTestId={`edit-${user.email}`}
            aria-label={`edit-${user.email}`}
            tooltipContent="Edit User"
          >
            <Edit fontSize="small" style={{ color: theme.palette.grey[500] }} />
          </GenericButton>
          {!isCurrentUser &&
            (isArchived ? (
              <GenericButton
                size="small"
                onClick={() => handleUnarchiveUser(user.id)}
                dataTestId={`unarchive-${user.email}`}
                aria-label={`unarchive-${user.email}`}
                tooltipContent="Unarchive User"
              >
                <UnarchiveOutlined
                  fontSize="small"
                  style={{ color: theme.palette.grey[700] }}
                />
              </GenericButton>
            ) : (
              <GenericButton
                size="small"
                onClick={() => handleArchiveUser(user.id)}
                dataTestId={`archive-${user.email}`}
                aria-label={`archive-${user.email}`}
                tooltipContent="Archive User"
              >
                <ArchiveOutlined
                  fontSize="small"
                  style={{ color: theme.palette.grey[700] }}
                />
              </GenericButton>
            ))}
        </div>
      ),
    };
  });

  return (
    <div
      style={{
        width: "100%",
        ...(isTestEnvironment ? { minWidth: 800 } : {}),
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGridPremium
        rows={defineRows}
        getRowClassName={(params) =>
          params.row.archived ? "archived-user-row" : ""
        }
        columnVisibilityModel={{
          id: false,
        }}
        columns={[
          {
            field: "id",
            headerName: "ID",
            hideable: true,
          },
          {
            field: "name",
            headerName: "Name",
            flex: 1,
          },
          {
            field: "email",
            headerName: "Email",
            flex: 1,
          },
          {
            field: "createdAt",
            headerName: "User Since",
            flex: 1,
          },
          {
            field: "groups",
            headerName: "Groups",
            renderCell: (params) => {
              const memberships = params.value;
              if (!memberships || memberships.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1">
                  {memberships.map((m: any) => (
                    <Chip
                      key={m.id}
                      label={m.functionalGroup.acronym}
                      size="small"
                      title={m.functionalGroup.name}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </div>
              );
            },
            flex: 1,
          },
          {
            field: "status",
            headerName: "Status",
            renderCell: (params) => params.value,
            width: 100,
          },
          {
            field: "actions",
            headerName: "",
            renderCell: (params) => params.value,
            align: "right",
            headerAlign: "right",
            width: 120,
          },
        ]}
        sx={{
          lineHeight: 2,
          fontSize: "1rem",
          ".MuiDataGrid-overlayWrapper": {
            height: 100,
          },
          ".MuiDataGrid-cell:not(:last-child), .MuiDataGrid-columnHeader:not(:last-child)":
            {
              borderRight: "none",
            },
          "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
            py: "8px",
          },
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
            py: "9px",
          },
          "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
            py: "22px",
          },
          ".MuiDataGrid-editInputCell": {
            fontSize: "1rem",
          },
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
          },
          ".MuiDataGrid-cellContent, .MuiDataGrid-columnHeaderTitle": {
            textOverflow: "clip",
          },
          ".MuiDataGrid-virtualScroller": {
            overflow: "hidden",
          },
          "& .MuiDataGrid-filler": {
            borderBottom: "none",
          },
          ".MuiDataGrid-cell.MuiDataGrid-cellEmpty": {
            border: "none",
          },
          ".MuiDataGrid-container--top [role=row]": {
            backgroundColor: "transparent",
          },
          "& .archived-user-row": {
            bgcolor: theme.palette.grey[100],
            color: theme.palette.grey[500],
            "&:hover": {
              bgcolor: theme.palette.grey[100],
            },
          },
          ...(disableCellSelection && {
            ".MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }),
        }}
        ignoreValueFormatterDuringExport
        getRowHeight={() => "auto"}
        autoHeight
        autosizeOnMount={!isTestEnvironment}
        disableVirtualization={isTestEnvironment}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooterRowCount
        hideFooter
        scrollbarSize={0}
        disableColumnReorder
      />
    </div>
  );
}
